"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "./AuthProvider";

interface WatchlistButtonProps {
  danchiId: string;
  source: "ur" | "jkk";
  danchiName: string;
  currentPath: string;
}

export function WatchlistButton({
  danchiId,
  source,
  danchiName,
  currentPath,
}: WatchlistButtonProps) {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const [registered, setRegistered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (authLoading || !user) return;

    fetch("/api/watchlist")
      .then((res) => res.json())
      .then((data) => {
        const found = data.items?.some(
          (item: { danchiId: string; source: string }) =>
            item.danchiId === danchiId && item.source === source
        );
        setRegistered(!!found);
      })
      .finally(() => setChecking(false));
  }, [user, authLoading, danchiId, source]);

  if (authLoading || (user && checking)) {
    return (
      <div className="h-10 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
    );
  }

  // 未ログイン
  if (!user) {
    return (
      <button
        onClick={() =>
          router.push(
            `/signup?redirect=${encodeURIComponent(currentPath)}`
          )
        }
        className="w-full flex items-center justify-center gap-2 bg-accent border border-accent text-white rounded-lg py-3 text-sm font-medium hover:bg-accent-hover hover:border-accent-hover transition-colors"
      >
        空室通知を受け取る
        <BellIcon />
      </button>
    );
  }

  // 登録済み
  if (registered) {
    return (
      <button
        onClick={handleRemove}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-700 text-muted hover:text-red-600 hover:border-red-300 rounded-lg py-3 text-sm font-medium transition-colors disabled:opacity-50"
      >
        {loading ? "解除中..." : "空室通知を解除"}
      </button>
    );
  }

  // 未登録
  return (
    <button
      onClick={handleAdd}
      disabled={loading}
      className="w-full flex items-center justify-center gap-2 bg-accent border border-accent text-white rounded-lg py-3 text-sm font-medium hover:bg-accent-hover hover:border-accent-hover transition-colors disabled:opacity-50"
    >
      {loading ? "登録中..." : "空室通知を受け取る"}
      <BellIcon />
    </button>
  );

  async function handleAdd() {
    setLoading(true);
    const res = await fetch("/api/watchlist", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ danchiId, source }),
    });
    if (res.ok) {
      setRegistered(true);
    } else {
      const data = await res.json();
      alert(data.error || "登録に失敗しました");
    }
    setLoading(false);
  }

  async function handleRemove() {
    setLoading(true);
    const res = await fetch("/api/watchlist", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ danchiId, source }),
    });
    if (res.ok) {
      setRegistered(false);
    }
    setLoading(false);
  }
}

function BellIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}
